import type { DashboardShiftStatus, DashboardStatusGroup } from "./types";

const LATE_THRESHOLD_MINUTES = 10;
const DUE_SOON_WINDOW_MINUTES = 30;
const SHORT_SHIFT_THRESHOLD = 0.9;
const SHORT_SHIFT_MIN_EXPECTED_MINUTES = 30;

interface ShiftTimingInput {
  readonly shiftStartUtc: Date;
  readonly shiftEndUtc: Date;
  readonly checkInTime: Date | null;
  readonly checkOutTime: Date | null;
}

export function deriveShiftStatus(
  input: ShiftTimingInput,
  now: Date,
): DashboardShiftStatus {
  const { shiftStartUtc, shiftEndUtc, checkInTime, checkOutTime } = input;

  if (checkOutTime !== null && checkInTime !== null) {
    const expectedMinutes = (shiftEndUtc.getTime() - shiftStartUtc.getTime()) / 60_000;
    const actualMinutes = (checkOutTime.getTime() - checkInTime.getTime()) / 60_000;
    if (expectedMinutes >= SHORT_SHIFT_MIN_EXPECTED_MINUTES && actualMinutes < expectedMinutes * SHORT_SHIFT_THRESHOLD) {
      return "short";
    }
    return "completed";
  }

  if (checkInTime !== null) {
    const lateThreshold = new Date(
      shiftStartUtc.getTime() + LATE_THRESHOLD_MINUTES * 60_000,
    );
    return checkInTime > lateThreshold ? "late" : "on_site";
  }

  const minutesSinceStart =
    (now.getTime() - shiftStartUtc.getTime()) / 60_000;

  if (minutesSinceStart > LATE_THRESHOLD_MINUTES) {
    return "no_show";
  }

  const minutesUntilStart = -minutesSinceStart;
  if (minutesUntilStart < DUE_SOON_WINDOW_MINUTES) {
    return "due_soon";
  }

  return "upcoming";
}

export function statusToGroup(status: DashboardShiftStatus): DashboardStatusGroup {
  switch (status) {
    case "no_show":
    case "late":
    case "short":
      return "needs_attention";
    case "due_soon":
      return "upcoming";
    case "on_site":
      return "on_site";
    case "completed":
      return "completed";
    case "upcoming":
      return "upcoming";
  }
}

export function isOnTime(
  shiftStartUtc: Date,
  checkInTime: Date,
): boolean {
  const lateThreshold = new Date(
    shiftStartUtc.getTime() + LATE_THRESHOLD_MINUTES * 60_000,
  );
  return checkInTime <= lateThreshold;
}

export function computeDurationMinutes(
  checkInTime: Date,
  checkOutTime: Date | null,
  now: Date,
): number {
  const end = checkOutTime ?? now;
  return Math.round((end.getTime() - checkInTime.getTime()) / 60_000);
}
